import { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  Autocomplete,
  DialogContent,
  DialogActions,
  FormHelperText,
  IconButton,
  Box
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { API } from '../../../../api';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { popupCountActions } from '../../../../redux/slices/poupsCountsSlice';
import { getUserDetails } from '../../../../utils/orgName';
import useProfileDetails from '../../../../hooks/useProfileDetails';
import { userActions } from '../../../../redux/slices/user-profile/userProfieSlice';

const LearningObjectives = () => {
  const { ftue, objectivesNumber, learningObjectives } = useProfileDetails();
  const dispatch = useAppDispatch();
  const { orgId, userId } = getUserDetails();
  const selectedGroupId = useAppSelector((state) => state.selectedGroup.data);
  const { lastseen } = useProfileDetails();
  const grpId = lastseen?.id || '';
  const isMentorLastSeen = lastseen?.role === 'mentor';

  const popuplength = useAppSelector((state) => state.popupsCount.popuplength);
  const count = useAppSelector((state) => state.popupsCount.count);
  const nextPopUpOrNot = popuplength - count;

  const getLocalStorageKey = (key: string) => `learningObjectives_${userId}_${key}`;
  const getInitialState = (key: string, defaultValue: any) => {
    const localStorageKey = getLocalStorageKey(key);
    const savedValue = localStorage.getItem(localStorageKey);
    return savedValue ? JSON.parse(savedValue) : defaultValue;
  };
  const getObjectivesProfile = () => {
    if (learningObjectives && Array.isArray(learningObjectives) && learningObjectives.length > 0) {
      return learningObjectives.map((obj) => obj.objective);
    }
    return [];
  };
  const [learningObjectiveCount, setLearningObjectiveCount] = useState(
    getInitialState('learningObjectiveCount', objectivesNumber || 3)
  );
  const [selectedObjectives, setSelectedObjectives] = useState<string[]>(
    getObjectivesProfile().length > 0 ? getObjectivesProfile() : getInitialState('selectedObjectives', [])
  );
  const [displayedObjectivesCount, setDisplayedObjectivesCount] = useState(
    Math.max(getInitialState('displayedObjectivesCount', objectivesNumber || 3), selectedObjectives.length || objectivesNumber || 3)
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ftueLoading, setFtueLoading] = useState(false);
  const [objectives, setObjectives] = useState<string[]>([]);
  const [maxObjectives, setMaxObjectives] = useState(getInitialState('maxObjectives', 10));
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const profileObjectives = getObjectivesProfile();
    if (profileObjectives.length > 0) {
      setSelectedObjectives(profileObjectives);

      if (profileObjectives.length > displayedObjectivesCount) {
        setDisplayedObjectivesCount(profileObjectives.length);
      }
      localStorage.setItem(getLocalStorageKey('selectedObjectives'), JSON.stringify(profileObjectives));
    }
  }, [learningObjectives]);

  useEffect(() => {
    const fetchMaxObjectives = async () => {
      try {
        const response = await API.getLearningObjectives({
          grpId: grpId,
          orgId: orgId
        });
        const objectivesData = response?.data?.data?.learningObjectives?.objectives || [];
        setMaxObjectives(objectivesData.length);

        localStorage.setItem(getLocalStorageKey('maxObjectives'), JSON.stringify(objectivesData.length));
      } catch (error) {
        console.error('Error fetching learning objectives:', error);
      }
    };

    if (selectedGroupId && orgId) {
      fetchMaxObjectives();
    }
  }, [selectedGroupId, orgId]);

  useEffect(() => {
    localStorage.setItem(getLocalStorageKey('selectedObjectives'), JSON.stringify(selectedObjectives));
    localStorage.setItem(getLocalStorageKey('displayedObjectivesCount'), JSON.stringify(displayedObjectivesCount));
    localStorage.setItem(getLocalStorageKey('learningObjectiveCount'), JSON.stringify(learningObjectiveCount));
  }, [selectedObjectives, displayedObjectivesCount, learningObjectiveCount, userId]);

  useEffect(() => {
    const fetchLearningObjectives = async () => {
      try {
        const storedObjectives = localStorage.getItem(getLocalStorageKey('objectives'));
        // const storedMax = localStorage.getItem(getLocalStorageKey('maxObjectives'));
        if (storedObjectives) {
          setObjectives(JSON.parse(storedObjectives));
          // setMaxObjectives(JSON.parse(storedMax));
          return;
        }

        const response = await API.getUserLearningObjectives({ orgId });
        const objectivesData = response?.data?.data ?? [];

        if (Array.isArray(objectivesData)) {
          const objectiveList = objectivesData.map((obj) => obj.objective);
          // const calculatedMax = objectivesData.length > 0 ? objectivesData.length : 10;

          setObjectives(objectiveList);
          //setMaxObjectives(calculatedMax);

          localStorage.setItem(getLocalStorageKey('objectives'), JSON.stringify(objectiveList));
          //localStorage.setItem(getLocalStorageKey('maxObjectives'), JSON.stringify(calculatedMax));
        }
      } catch (error) {
        console.error('Error fetching learning objectives:', error);
        setObjectives([]);
      }
    };
    fetchLearningObjectives();
  }, [orgId]);

  useEffect(() => {
    if (objectivesNumber) {
      setLearningObjectiveCount(objectivesNumber);
      setDisplayedObjectivesCount(objectivesNumber);
    }
  }, [objectivesNumber]);

  const handleInputChange = (index: number, value: string) => {
    setSelectedObjectives((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated.filter((obj) => obj !== null && obj !== undefined);
    });
  };

  const checkErrors = () => {
    const hasObjectives = selectedObjectives.some((obj) => obj && obj.trim() !== '');

    if (!hasObjectives) {
      return true;
    }
    return false;
  };

  const updateFtue = async () => {
    setFtueLoading(true);
    try {
      const response = await API.updateUserFtue({ ftue: false, userId });
      if (response.status === 200) {
        localStorage.removeItem(getLocalStorageKey('selectedObjectives'));
        localStorage.removeItem(getLocalStorageKey('learningObjectiveCount'));
        localStorage.removeItem(getLocalStorageKey('displayedObjectivesCount'));
        localStorage.removeItem(getLocalStorageKey('objectives'));
        dispatch(userActions.userFtueUpdate(0));
        setFtueLoading(false);
      }
    } catch (e) {
      toast.error('Failed to update user status');
      setFtueLoading(false);
    }
  };

  const onSubmit = async () => {
    if (checkErrors()) {
      toast.error(`Select at least one ${isMentorLastSeen ? 'area of expertise' : 'learning objective'}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const objectivesToSave = selectedObjectives.filter((objective) => objective !== '');

      const response = await API.getUserLearningObjectives({ orgId });
      const allObjectives = response?.data?.data || [];

      const existingObjectivesMap = new Map(
        (learningObjectives || []).map(obj => [obj.objective, obj])
      );
  
      const updatedObjectives = objectivesToSave.map(objective => {
        if (existingObjectivesMap.has(objective)) {
          return existingObjectivesMap.get(objective);
        }
        return allObjectives.find((obj: any) => obj.objective === objective);
      }).filter(obj => obj !== undefined); 
  
      const payload = {
        learningObjectives: updatedObjectives
      };

      const saveResponse = await API.saveLearningObjectives(payload);
      if (saveResponse.status === 200) {
        dispatch(userActions.updateObjectives(updatedObjectives));
        localStorage.setItem(getLocalStorageKey('selectedObjectives'), JSON.stringify(objectivesToSave));
      }

      if (ftue && nextPopUpOrNot !== 1) {
        dispatch(popupCountActions.increment(0));
      }
      if (nextPopUpOrNot === 1) {
        await updateFtue();
      }
    } catch (error) {
      console.error('Error saving learning objectives:', error);
      toast.error('An error occurred while saving');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFilteredOptions = (index: number) => {
    const filteredOptions = objectives.filter(
      (option) => !selectedObjectives.includes(option) || selectedObjectives[index] === option
    );
    // if (
    //   inputValue &&
    //   inputValue.trim() !== '' &&
    //   !filteredOptions.includes(inputValue) &&
    //   !objectives.includes(inputValue)
    // ) {
    //   return [...filteredOptions, `Add "${inputValue}"`];
    // }
    return filteredOptions;
  };

  const handleOptionSelection = (index: number, newValue: string | null) => {
    setSelectedObjectives((prev) => {
      const updated = [...prev];
      while (updated.length <= index) {
        updated.push('');
      }

      if (newValue === null) {
        updated[index] = '';
      } else {
        updated[index] = newValue;
      }

      return updated;
    });
    setInputValue('');
  };

  useEffect(() => {
    if (selectedObjectives.length > displayedObjectivesCount) {
      setDisplayedObjectivesCount(selectedObjectives.length);
    }
  }, [selectedObjectives.length, displayedObjectivesCount]);

  const addObjectiveField = () => {
    if (displayedObjectivesCount < maxObjectives) {
      setDisplayedObjectivesCount((prev) => prev + 1);
      setLearningObjectiveCount((prev: any) => prev + 1);
      // if (selectedObjectives.length < displayedObjectivesCount + 1) {
      //   setSelectedObjectives((prev) => [...prev, '']);
      // }
    }
  };

  const getDialogActions = () => {
    return (
      <DialogActions>
        {ftue && (
          <LoadingButton
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={() => {
              localStorage.setItem(
                getLocalStorageKey('learningObjectiveCount'),
                JSON.stringify(learningObjectiveCount)
              );
              localStorage.setItem(
                getLocalStorageKey('displayedObjectivesCount'),
                JSON.stringify(displayedObjectivesCount)
              );
              localStorage.setItem(getLocalStorageKey('selectedObjectives'), JSON.stringify(selectedObjectives));
              localStorage.setItem(getLocalStorageKey('maxObjectives'), JSON.stringify(maxObjectives));
              dispatch(popupCountActions.decrement(0));
            }}
          >
            Back
          </LoadingButton>
        )}

        <LoadingButton
          color="primary"
          variant="contained"
          fullWidth
          loading={isSubmitting || ftueLoading}
          onClick={onSubmit}
        >
          {nextPopUpOrNot === 1 ? 'Finish' : nextPopUpOrNot > 1 ? 'Next' : 'Save'}
        </LoadingButton>
      </DialogActions>
    );
  };
  return (
    <>
      <DialogContent>
        <Grid container spacing={2}>
          {Array.from({ length: displayedObjectivesCount }).map((_, index) => (
            <Grid item xs={12} key={index}>
              <InputLabel>{`${isMentorLastSeen ? 'Area of expertise' : 'Learning Objective'} ${index + 1}`}</InputLabel>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  size="small"
                  id={`learning-objective-${index}`}
                  value={selectedObjectives[index] || ''}
                  options={getFilteredOptions(index)}
                  onChange={(_, newValue) => handleOptionSelection(index, newValue)}
                  onInputChange={(_, newInputValue) => {
                    setInputValue(newInputValue);
                    handleInputChange(index, newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder={`Select ${isMentorLastSeen ? 'area of expertise' : 'learning Objective'} ${
                        index + 1
                      }`}
                      error={!!errors[index]}
                    />
                  )}
                />
                <FormHelperText error>{errors[index]}</FormHelperText>
              </FormControl>
            </Grid>
          ))}
        </Grid>

        {isMentorLastSeen && displayedObjectivesCount < maxObjectives && (
          <Box mt={2}>
            <IconButton size="small" onClick={addObjectiveField}>
              <AddCircleIcon fontSize="large" color="info" />
            </IconButton>
            <Typography variant="body2" component="span" sx={{ ml: 1 }}>
              Add another area of expertise
            </Typography>
          </Box>
        )}
      </DialogContent>

      {getDialogActions()}
    </>
  );
};

export default LearningObjectives;
